<template>
    <div>
        <CompassTooltip placement="top" :delay="{ show: 0, hide: 1 }">
            <template #content>
                <span>{{ $l10n.__('Saisie de temps') }}</span>
                <span class="timetracking-shortcut">
                    {{ shortcut }}
                </span>
            </template>
            <CompassIconButton
                ref="timetrackingRichTextTrigger"
                icon="performance"
                size="small"
                :active="localShowMenuTimetracking || isActive"
                @click="toggleTimetrackingForm"
            />
        </CompassTooltip>
        <CompassPopover
            :title="$t.__('richtext.customoptions.TimetrackingRichText.track_time')"
            customClass="custom-popover-timetracking"
            :isVisible="localShowMenuTimetracking"
            :triggerRef="triggerRefEl"
            noCloseOnClickOutside=".autocomplete-elselect"
            @close="hideMenuTimetracking"
        >
            <div class="timetracking-form-container">
                <CompassFormItem
                    :label="$t.__('richtext.customoptions.TimetrackingRichText.time_passed')"
                    required
                    :requiredLabel="$t.__('common.labels.required')"
                    class="timetracking-form-item-time"
                >
                    <TimeInput ref="refTimeInput" v-model="valueTime" :mode="mode" />
                </CompassFormItem>
                <CompassFormItem
                    :label="$t.__('richtext.customoptions.TimetrackingRichText.select_service')"
                    required
                    :requiredLabel="$t.__('common.labels.required')"
                    class="timetracking-form-item-service"
                >
                    <AutocompleteRemoteInput
                        v-model="valueService"
                        :mode="mode"
                        :placeholder="
                            $t.__('richtext.customoptions.TimetrackingRichText.select_service')
                        "
                        :limit="1"
                        route="/rest/catalogue/items/autocomplete"
                        :allowedType="['service']"
                        :queryParameters="{ timetracking: true }"
                        :defaultOptions="defaultOptions"
                        :searchOnFocus="true"
                        popperClass="autocomplete-elselect"
                    >
                    </AutocompleteRemoteInput>
                </CompassFormItem>
            </div>
        </CompassPopover>
    </div>
</template>

<script>
import TimeInput from 'vcomponents/inputs/TimeInput.vue';
import AutocompleteRemoteInput from 'vcomponents/inputs/AutocompleteRemoteInput.vue';
import { normalizeKeyName } from 'Helpers/normalizeKeyHelper';
import { isMacOS } from 'Helpers/userAgentHelper.js';

export default {
    name: 'TimetrackingRichText',
    components: {
        TimeInput,
        AutocompleteRemoteInput,
    },
    props: {
        modelValue: {
            type: [Object, Array, Boolean],
            required: true,
        },
        showMenuTimetracking: {
            type: Boolean,
            required: true,
        },
        mode: {
            type: String,
            required: true,
        },
        defaultOptions: {
            type: Array,
            required: true,
        },
        refRichText: {
            type: String,
            required: true,
        },
    },
    emits: ['update:modelValue', 'close'],
    data() {
        return {
            localShowMenuTimetracking: this.showMenuTimetracking,
            displayForm: true,
            triggerRefEl: null,
        };
    },
    computed: {
        valueTime: {
            get() {
                const value = this.modelValue.duration || this.modelValue.timetrackingTime;
                return value ?? '';
            },
            set(newValue) {
                this.$emit('update:modelValue', {
                    ...this.modelValue,
                    duration: newValue,
                    timetrackingTime: newValue,
                });
            },
        },
        valueService: {
            get() {
                return this.modelValue.service ?? '';
            },
            set(newValue) {
                this.$emit('update:modelValue', { ...this.modelValue, service: newValue });
            },
        },
        isActive() {
            // init or reset value => false
            const hasValueTime = this.valueTime !== '' && this.valueTime !== ':00';
            const hasValueService = Array.isArray(this.modelValue.service)
                ? this.modelValue.service.length > 0
                : this.valueService !== '' && this.valueService !== 0;

            return hasValueTime && hasValueService;
        },
        shortcut() {
            return `${normalizeKeyName('Ctrl')} + Shift + $`;
        },
    },
    created() {
        window.addEventListener('keydown', this.handleKeyDown);
        EventBus.on('annotationTimetracking:reset', () => {
            this.displayForm = false;
        });
    },
    mounted() {
        this.triggerRefEl = this.$refs.timetrackingRichTextTrigger;
    },
    unmounted() {
        EventBus.off('annotationTimetracking:reset');
    },
    methods: {
        toggleTimetrackingForm() {
            if (this.localShowMenuTimetracking) {
                this.hideMenuTimetracking();
                return;
            }
            this.displayForm = true;
            this.localShowMenuTimetracking = true;
            this.$nextTick(() => {
                this.$refs.refTimeInput?.setFocus();
            });
        },
        hideMenuTimetracking() {
            this.localShowMenuTimetracking = false;
            this.$emit('close');
            this.$parent.$parent.$el.querySelector('.tiptap')?.focus();
        },
        // Check witch RichText is focused
        checkRichTextfocused() {
            const focusedElement = document.activeElement;
            const parentFocusedElement = focusedElement.closest('[data-ref]');
            const dataRefValue = parentFocusedElement
                ? parentFocusedElement.getAttribute('data-ref')
                : null;
            return dataRefValue;
        },
        // Add shortcut to open timetracking form with ctrl + shift + $
        handleKeyDown(event) {
            if (event.key === 'Escape') {
                this.hideMenuTimetracking();
            }

            if (
                ((!isMacOS() && event.key === '£') || (isMacOS() && event.key === '*')) &&
                event.shiftKey &&
                (event.ctrlKey || (isMacOS() && event.metaKey))
            ) {
                if (this.localShowMenuTimetracking) {
                    return this.hideMenuTimetracking();
                }

                event.preventDefault();

                if (this.checkRichTextfocused() !== this.refRichText) {
                    return;
                } else {
                    this.toggleTimetrackingForm();
                }
            }
        },
    },
    onBeforeDestroy() {
        window.removeEventListener('keydown', this.handleKeyDown);
    },
};
</script>

<style lang="scss">
.custom-popover-timetracking {
    min-width: 220px;
    width: auto;

    .timetracking-form-item-time {
        margin: 0 0 var(--spacing-1);
    }
    .timetracking-form-item-service {
        margin: 0 0 var(--spacing-0);
    }

    .time-input .w-240 {
        width: 100% !important;
    }
}
.timetracking-select-service {
    z-index: 1001 !important;
}
.timetracking-shortcut {
    color: var(--colors-common-text-contrast-caption) !important;
}
</style>
